/**
 * Claims Knowledge Base — Know-How Bereich für das Claims-Team.
 * Markdown-basierte Artikel, gruppiert nach Carrier.
 * @page /claims/knowledge
 * @roles alle (lesen), admin/manager (bearbeiten)
 * @i18n claims.knowledge
 * @endpoints GET/POST/PUT/DELETE /api/claims-knowledge
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../../context/LanguageContext';
import { PageHeader, Button } from '../../components/ui';
import {
 BookOpen, Plus, Edit3, Trash2, X, ChevronRight,
 Eye, EyeOff, ArrowLeft, Save, FileText, Layers,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function api(path, opts = {}) {
 const hasBody = opts.body != null;
 const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
 return fetch(`${API}${path}`, {
 ...opts,
 credentials: 'include',
 headers: { ...headers, ...opts.headers },
 }).then(async (r) => {
 const data = await r.json();
 if (!r.ok) throw new Error(data.error || data.message || `HTTP ${r.status}`);
 return data;
 });
}

// ── Markdown renderers (same as Handbuch) ──

const mdComponents = {
 blockquote: ({ children }) => {
 const extractText = (node) => {
 if (typeof node === 'string') return node;
 if (!node) return '';
 if (node.props?.children) {
 const c = node.props.children;
 if (Array.isArray(c)) return c.map(extractText).join('');
 return extractText(c);
 }
 return '';
 };
 const text = Array.isArray(children) ? children.map(extractText).join('') : extractText(children);
 if (text.includes('\u{1F4A1}') || text.includes('Tipp')) {
 return (
 <div className="bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-500 rounded-r-lg p-4 my-4 not-prose">
 <div className="text-sm text-emerald-900 dark:text-emerald-200 [&>p]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&_strong]:text-emerald-800 dark:[&_strong]:text-emerald-100">{children}</div>
 </div>
 );
 }
 if (text.includes('\u26A0') || text.includes('Wichtig') || text.includes('Achtung')) {
 return (
 <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-r-lg p-4 my-4 not-prose">
 <div className="text-sm text-amber-900 dark:text-amber-200 [&>p]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&_strong]:text-amber-800 dark:[&_strong]:text-amber-100">{children}</div>
 </div>
 );
 }
 return (
 <div className="bg-[var(--bg-inset,#F8F9FC)] dark:bg-[rgba(31,41,55,0.5)] border-l-4 border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)] rounded-r-lg p-4 my-4 not-prose">
 <div className="text-sm text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)] italic [&>p]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0">{children}</div>
 </div>
 );
 },
 h1: ({ children }) => <h1 className="text-xl font-bold mt-6 mb-3 text-[var(--text-primary)]">{children}</h1>,
 h2: ({ children }) => <h2 className="text-lg font-bold mt-6 mb-3 pb-2 border-b border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)] text-[var(--text-primary)]">{children}</h2>,
 h3: ({ children }) => <h3 className="text-base font-medium mt-5 mb-2 text-[var(--text-primary)]">{children}</h3>,
 ul: ({ children }) => <ul className="list-disc list-outside pl-5 space-y-1.5 my-3 text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)]">{children}</ul>,
 ol: ({ children }) => <ol className="list-decimal list-outside pl-5 space-y-2 my-3 text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)]">{children}</ol>,
 li: ({ children }) => <li className="text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)] leading-relaxed">{children}</li>,
 p: ({ children }) => <p className="my-2 text-[13px] leading-relaxed text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)]">{children}</p>,
 a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#338edc] hover:underline">{children}</a>,
 code: ({ inline, className, children }) => {
 if (inline !== false && !className) {
 return <code className="bg-[var(--bg-inset,#F8F9FC)] dark:bg-[var(--bg-inset,#F8F9FC)] text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>;
 }
 return <pre className="bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-primary,#1A1A2E)] rounded-lg p-4 overflow-x-auto my-4"><code className="text-sm font-mono">{children}</code></pre>;
 },
 table: ({ children }) => (
 <div className="overflow-x-auto my-4 rounded-card border border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)]">
 <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">{children}</table>
 </div>
 ),
 thead: ({ children }) => <thead className="bg-[var(--bg-inset,#F8F9FC)]">{children}</thead>,
 th: ({ children }) => <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-secondary,#4B5563)] dark:text-[var(--text-tertiary,#6B7280)] uppercase tracking-wider">{children}</th>,
 td: ({ children }) => <td className="px-4 py-2.5 text-sm text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)] border-t border-[var(--border-table,#F3F4F6)] dark:border-[var(--border-table,#F3F4F6)]">{children}</td>,
 strong: ({ children }) => <strong className="font-semibold text-[var(--text-primary)]">{children}</strong>,
 hr: () => <hr className="my-6 border-[var(--border-default,#E5E7EB)] dark:border-[var(--border-default,#E5E7EB)]" />,
};

export default function ClaimsKnowledge() {
 const { t } = useLanguage();
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();

 const [articles, setArticles] = useState([]);
 const [carriers, setCarriers] = useState([]);
 const [selectedId, setSelectedId] = useState(null);
 const [detail, setDetail] = useState(null);
 const [loading, setLoading] = useState(true);
 const [showEditor, setShowEditor] = useState(false);
 const [editData, setEditData] = useState(null);
 const [saving, setSaving] = useState(false);
 const [previewMode, setPreviewMode] = useState(false);
 const [userRole, setUserRole] = useState(null);

 const isEditor = ['admin', 'manager'].includes(userRole);

 // Load articles + carriers + user role
 const load = useCallback(async () => {
 try {
 setLoading(true);
 const [arts, cfg] = await Promise.all([
 api('/api/claims-knowledge'),
 api('/api/claims-portal/config'),
 ]);
 setArticles(arts);
 setCarriers(cfg.carriers || []);
 } catch (err) {
 console.error('Knowledge load error:', err);
 } finally {
 setLoading(false);
 }
 }, []);

 useEffect(() => {
 load();
 // Get user role from cookie/auth
 fetch(`${API}/api/auth/me`, { credentials: 'include' })
 .then(r => r.json())
 .then(u => setUserRole(u.rolle || null))
 .catch(() => {});
 }, [load]);

 // Auto-select article from URL ?article=ID
 useEffect(() => {
 const artId = searchParams.get('article');
 if (artId && articles.length > 0 && !selectedId) {
 setSelectedId(parseInt(artId));
 }
 }, [searchParams, articles, selectedId]);

 // Load article detail
 useEffect(() => {
 if (!selectedId) { setDetail(null); return; }
 api(`/api/claims-knowledge/${selectedId}`)
 .then(d => setDetail(d))
 .catch(err => console.error(err));
 }, [selectedId]);

 // Group articles by carrier
 const grouped = {};
 const generalArticles = [];
 for (const art of articles) {
 if (!art.carrier_id) {
 generalArticles.push(art);
 } else {
 const key = art.carrier_name || `Carrier #${art.carrier_id}`;
 if (!grouped[key]) grouped[key] = { carrier_id: art.carrier_id, articles: [] };
 grouped[key].articles.push(art);
 }
 }

 // Editor form
 const initForm = { title: '', carrier_id: '', claim_type: '', content: '', sort_order: 0, is_published: true };

 function openNew() {
 setEditData(initForm);
 setShowEditor(true);
 setPreviewMode(false);
 }

 function openEdit(art) {
 setEditData({
 id: art.id,
 title: art.title || '',
 carrier_id: art.carrier_id || '',
 claim_type: art.claim_type || '',
 content: art.content || '',
 sort_order: art.sort_order || 0,
 is_published: art.is_published !== false,
 });
 setShowEditor(true);
 setPreviewMode(false);
 }

 async function saveArticle() {
 if (!editData.title || !editData.content) return;
 setSaving(true);
 try {
 const payload = {
 title: editData.title,
 carrier_id: editData.carrier_id || null,
 claim_type: editData.claim_type || null,
 content: editData.content,
 sort_order: parseInt(editData.sort_order) || 0,
 is_published: editData.is_published,
 };
 if (editData.id) {
 await api(`/api/claims-knowledge/${editData.id}`, { method: 'PUT', body: payload });
 } else {
 await api('/api/claims-knowledge', { method: 'POST', body: payload });
 }
 setShowEditor(false);
 setEditData(null);
 await load();
 } catch (err) {
 alert(err.message);
 } finally {
 setSaving(false);
 }
 }

 async function deleteArticle(id) {
 if (!confirm(t('claims.knowledge.deleteArticle') + '?')) return;
 try {
 await api(`/api/claims-knowledge/${id}`, { method: 'DELETE' });
 if (selectedId === id) { setSelectedId(null); setDetail(null); }
 await load();
 } catch (err) {
 alert(err.message);
 }
 }

 // Editor view
 if (showEditor) {
 return (
 <div className="space-y-6">
 <PageHeader
 title={editData?.id ? t('claims.knowledge.editArticle') : t('claims.knowledge.newArticle')}
 icon={BookOpen}
 >
 <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={() => setShowEditor(false)}>
 {t('common.back') || 'Zurück'}
 </Button>
 </PageHeader>

 <div className="bg-surface-card border border-line-card rounded-card p-6 space-y-4">
 {/* Title */}
 <div>
 <label className="block text-[12px] font-medium text-content-secondary mb-1">{t('claims.knowledge.articleTitle')}</label>
 <input
 value={editData.title}
 onChange={e => setEditData({ ...editData, title: e.target.value })}
 className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
 placeholder={t('claims.knowledge.articleTitle')}
 />
 </div>

 {/* Row: Carrier + Claim Type + Sort */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div>
 <label className="block text-[12px] font-medium text-content-secondary mb-1">{t('claims.knowledge.carrier')}</label>
 <select
 value={editData.carrier_id}
 onChange={e => setEditData({ ...editData, carrier_id: e.target.value })}
 className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
 >
 <option value="">{t('claims.knowledge.allCarriers')}</option>
 {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
 </select>
 </div>
 <div>
 <label className="block text-[12px] font-medium text-content-secondary mb-1">{t('claims.knowledge.claimType')}</label>
 <select
 value={editData.claim_type}
 onChange={e => setEditData({ ...editData, claim_type: e.target.value })}
 className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
 >
 <option value="">{t('claims.knowledge.bothTypes')}</option>
 <option value="damage">{t('claims.typeDamage')}</option>
 <option value="investigation">{t('claims.typeInvestigation')}</option>
 <option value="both">{t('claims.knowledge.bothTypes')}</option>
 </select>
 </div>
 <div>
 <label className="block text-[12px] font-medium text-content-secondary mb-1">{t('claims.knowledge.sortOrder')}</label>
 <input
 type="number"
 value={editData.sort_order}
 onChange={e => setEditData({ ...editData, sort_order: e.target.value })}
 className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
 />
 </div>
 </div>

 {/* Published toggle */}
 <label className="flex items-center gap-2 cursor-pointer">
 <input
 type="checkbox"
 checked={editData.is_published}
 onChange={e => setEditData({ ...editData, is_published: e.target.checked })}
 className="rounded text-[#338edc] focus:ring-[#338edc]"
 />
 <span className="text-[13px] text-content">{t('claims.knowledge.published')}</span>
 </label>

 {/* Content with preview toggle */}
 <div>
 <div className="flex items-center justify-between mb-1">
 <label className="text-[12px] font-medium text-content-secondary">{t('claims.knowledge.content')}</label>
 <button
 onClick={() => setPreviewMode(!previewMode)}
 className="flex items-center gap-1 text-[12px] text-[#338edc] hover:underline"
 >
 {previewMode ? <Edit3 size={12} /> : <Eye size={12} />}
 {previewMode ? 'Editor' : 'Preview'}
 </button>
 </div>
 {previewMode ? (
 <div className="min-h-[300px] p-4 bg-surface-card border border-line rounded-button prose prose-sm max-w-none dark:prose-invert">
 <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
 {editData.content}
 </ReactMarkdown>
 </div>
 ) : (
 <textarea
 value={editData.content}
 onChange={e => setEditData({ ...editData, content: e.target.value })}
 rows={16}
 className="w-full px-3 py-2 text-[13px] font-mono bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 resize-y"
 placeholder="# Titel&#10;&#10;Markdown-Inhalt..."
 />
 )}
 </div>

 {/* Save */}
 <div className="flex justify-end gap-3 pt-2">
 <Button variant="secondary" size="sm" onClick={() => setShowEditor(false)}>
 {t('common.cancel') || 'Abbrechen'}
 </Button>
 <Button size="sm" icon={Save} onClick={saveArticle} disabled={saving || !editData.title || !editData.content}>
 {saving ? '...' : (t('common.save') || 'Speichern')}
 </Button>
 </div>
 </div>
 </div>
 );
 }

 // Main view: sidebar + content
 return (
 <div className="p-6 space-y-6">
 <PageHeader title={t('claims.knowledge.title')} icon={BookOpen}>
 <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={() => navigate('/claims')}>
 {t('claims.title')}
 </Button>
 {isEditor && (
 <Button size="sm" icon={Plus} onClick={openNew}>
 {t('claims.knowledge.newArticle')}
 </Button>
 )}
 </PageHeader>

 {loading ? (
 <div className="flex items-center justify-center py-20 text-content-secondary text-sm">Loading...</div>
 ) : articles.length === 0 ? (
 <div className="bg-surface-card border border-line-card rounded-card p-12 text-center">
 <BookOpen className="mx-auto mb-3 text-content-tertiary" size={32} />
 <p className="text-[13px] text-content-secondary">{t('claims.knowledge.noArticles')}</p>
 {isEditor && (
 <Button size="sm" icon={Plus} className="mt-4" onClick={openNew}>
 {t('claims.knowledge.newArticle')}
 </Button>
 )}
 </div>
 ) : (
 <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
 {/* Sidebar */}
 <div className="bg-surface-card border border-line-card rounded-card p-4 h-fit lg:sticky lg:top-20">
 {/* General */}
 {generalArticles.length > 0 && (
 <div className="mb-4">
 <div className="text-[11px] font-semibold text-content-tertiary uppercase tracking-wider mb-2 px-2">
 {t('claims.knowledge.allCarriers')}
 </div>
 {generalArticles.map(art => (
 <SidebarItem
 key={art.id}
 article={art}
 active={selectedId === art.id}
 onClick={() => setSelectedId(art.id)}
 t={t}
 />
 ))}
 </div>
 )}

 {/* Carrier groups */}
 {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([carrierName, group]) => (
 <div key={group.carrier_id} className="mb-4">
 <div className="text-[11px] font-semibold text-content-tertiary uppercase tracking-wider mb-2 px-2">
 {carrierName}
 </div>
 {group.articles.map(art => (
 <SidebarItem
 key={art.id}
 article={art}
 active={selectedId === art.id}
 onClick={() => setSelectedId(art.id)}
 t={t}
 />
 ))}
 </div>
 ))}
 </div>

 {/* Content */}
 <div className="bg-surface-card border border-line-card rounded-card p-6 min-h-[400px]">
 {detail ? (
 <>
 <div className="flex items-start justify-between mb-4">
 <div>
 <h2 className="text-lg font-bold text-content">{detail.title}</h2>
 <div className="flex items-center gap-3 mt-1 text-[12px] text-content-tertiary">
 {detail.carrier_name && <span className="text-content-secondary">{detail.carrier_name}</span>}
 {detail.claim_type && detail.claim_type !== 'both' && (
 <span>{detail.claim_type === 'damage' ? t('claims.typeDamage') : t('claims.typeInvestigation')}</span>
 )}
 {!detail.is_published && (
 <span className="flex items-center gap-1 text-amber-500"><EyeOff size={12} /> Draft</span>
 )}
 </div>
 </div>
 {isEditor && (
 <div className="flex items-center gap-2">
 <button onClick={() => openEdit(detail)} className="p-1.5 rounded-lg hover:bg-surface-hover text-content-secondary hover:text-content transition-colors">
 <Edit3 size={14} />
 </button>
 <button onClick={() => deleteArticle(detail.id)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-content-secondary hover:text-red-500 transition-colors">
 <Trash2 size={14} />
 </button>
 </div>
 )}
 </div>
 <div className="prose prose-sm max-w-none dark:prose-invert">
 <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
 {detail.content}
 </ReactMarkdown>
 </div>
 {detail.updated_at && (
 <div className="mt-6 pt-4 border-t border-line text-[11px] text-content-tertiary">
 {t('common.lastUpdated') || 'Zuletzt aktualisiert'}: {new Date(detail.updated_at).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
 {detail.created_by_name && <span className="ml-2">von {detail.created_by_name}</span>}
 </div>
 )}
 </>
 ) : (
 <div className="flex flex-col items-center justify-center py-16 text-content-tertiary">
 <FileText size={32} className="mb-3" />
 <p className="text-[13px]">{t('claims.knowledge.selectArticle') || 'Artikel aus der Seitenleiste auswählen'}</p>
 </div>
 )}
 </div>
 </div>
 )}
 </div>
 );
}

function SidebarItem({ article, active, onClick, t }) {
 return (
 <button
 onClick={onClick}
 className={`w-full text-left px-3 py-2 rounded-lg text-[13px] transition-colors flex items-center gap-2 ${
 active
 ? 'bg-[#EBF4FD] dark:bg-[#338edc]/10 text-[#338edc] font-medium'
 : 'text-content-secondary hover:bg-surface-hover hover:text-content'
 }`}
 >
 <FileText size={13} className="shrink-0" />
 <span className="truncate">{article.title}</span>
 {!article.is_published && <EyeOff size={11} className="shrink-0 text-amber-500" />}
 </button>
 );
}
